import { useEffect, ReactElement } from "react";
import { Combobox, useCombobox } from "@twilio-paste/core/combobox";
import {
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
} from "@twilio-paste/core/dropdown-library";

import { types } from "../../definitions";

/**
 *  Behavior differences from default Paste Combobox component (as of this writing):
 *    - items menu does not open without a non-empty input nor when the items list is empty (no more awkward empty white dropdown!)
 *
 *  Things to note:
 *    - the consuming parent container is assumed to manage the state of the input
 *    - does not filter the items list based on the input, as it is assumed the parent will control the given items
 */
const ComboboxSearch = (props: types.ComboboxSearchProps): ReactElement => {
  const { items, onChange, onSelectItem, selectedItem, value, ...rest } = props;

  /**
   * This check ensures the autocomplete box opens/closes
   * when items change from prop updates
   */
  useEffect(() => {
    if (items.length) {
      openMenu();
    } else {
      closeMenu();
    }
  }, [items]);

  const handleSelectedItemChange = (
    changes: UseComboboxStateChange<any>
  ): void => {
    if (changes?.selectedItem !== null) {
      onSelectItem(changes?.selectedItem);
    }
  };

  const handleInputValueChange = ({
    inputValue,
  }: UseComboboxStateChange<any>): void => {
    if (inputValue !== undefined) {
      // pass back new input value to callback
      onChange(inputValue);
    }
  };

  const handleStateReducer = (
    state: UseComboboxState<any>,
    actionsAndChanges: UseComboboxStateChangeOptions<any>
  ): Partial<UseComboboxState<any>> => {
    const { changes } = actionsAndChanges;

    /**
     * This check ensures the autocomplete box does not open
     * when there is no input value and no items
     */
    const isOpen = changes?.isOpen && !!changes?.inputValue && !!items?.length;

    return {
      ...state,
      ...changes,
      isOpen,
    };
  };

  const { closeMenu, openMenu, ...state } = useCombobox({
    items,
    onSelectedItemChange: handleSelectedItemChange,
    onInputValueChange: handleInputValueChange,
    stateReducer: handleStateReducer,
    initialInputValue: value,
    selectedItem,
  });

  return <Combobox {...rest} state={state} items={items} autocomplete />;
};

export default ComboboxSearch;
